const KEY_TRUSTED = "sakhi_trusted_adult";
const KEY_SPEAKER = "sakhi_speaker";
const KEY_SESSION = "sakhi_session";
const KEY_PHOTO = "sakhi_photo";
const KEY_READING = "sakhi_reading_mode";
const KEY_ADMIN_PIN = "sakhi_admin_pin";
const KEY_ADMIN_UNLOCK = "sakhi_admin_unlock";
const KEY_WAKE = "sakhi_wake_word";
const KEY_WAKE_PHRASE = "sakhi_wake_phrase";  // string, e.g. "sakhi didi"
const KEY_PACE = "sakhi_speech_pace";
const KEY_BARGE_HINT_SHOWN = "sakhi_barge_hint_shown"; // count how many times girl has seen the hint

const DEFAULT_SPEAKER = "priya";
const DEFAULT_PACE = 1.0;
const DEFAULT_WAKE_PHRASE = "sakhi didi";
const BARGE_HINT_MAX = 3;
const UNLOCK_TTL_MS = 10 * 60 * 1000;

// Sarvam bulbul:v3 female voices. Old v2 names (anushka/manisha/vidya/arya)
// are auto-migrated to their v3 equivalents on read.
const LEGACY_SPEAKER_MAP = {
  anushka: "priya",
  manisha: "neha",
  vidya: "pooja",
  arya: "shreya",
};

export function getTrustedAdult() {
  try {
    const raw = localStorage.getItem(KEY_TRUSTED);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v?.phone) return null;
    return v;
  } catch { return null; }
}

export function setTrustedAdult(name, phone) {
  const clean = (phone || "").replace(/[^\d+]/g, "");
  if (!clean) { localStorage.removeItem(KEY_TRUSTED); return; }
  localStorage.setItem(KEY_TRUSTED, JSON.stringify({ name: name || "Trusted Adult", phone: clean }));
}

export function getVoiceName() {
  const stored = localStorage.getItem(KEY_SPEAKER) || DEFAULT_SPEAKER;
  return LEGACY_SPEAKER_MAP[stored] || stored;
}
export function setVoiceName(name) {
  if (!name) localStorage.removeItem(KEY_SPEAKER);
  else localStorage.setItem(KEY_SPEAKER, LEGACY_SPEAKER_MAP[name] || name);
}

export const SARVAM_FEMALE_SPEAKERS = [
  { id: "priya",  label: "Priya — warm and gentle" },
  { id: "neha",   label: "Neha — clear and confident" },
  { id: "pooja",  label: "Pooja — soft and caring" },
  { id: "shreya", label: "Shreya — bright and cheerful" },
];

export function getSakhiPhoto() { return localStorage.getItem(KEY_PHOTO) || ""; }
export function setSakhiPhoto(dataUrl) {
  if (!dataUrl) localStorage.removeItem(KEY_PHOTO);
  else localStorage.setItem(KEY_PHOTO, dataUrl);
}

export function getReadingMode() { return localStorage.getItem(KEY_READING) === "1"; }
export function setReadingMode(on) {
  if (on) localStorage.setItem(KEY_READING, "1");
  else localStorage.removeItem(KEY_READING);
}

export function getOrCreateSessionId() {
  let s = localStorage.getItem(KEY_SESSION);
  if (!s) {
    s = "sakhi-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem(KEY_SESSION, s);
  }
  return s;
}

// ---- Admin PIN ----
export function hasAdminPin() {
  return !!localStorage.getItem(KEY_ADMIN_PIN);
}
export function getAdminPin() {
  return localStorage.getItem(KEY_ADMIN_PIN) || "";
}
export function setAdminPin(pin) {
  const clean = (pin || "").replace(/\D/g, "").slice(0, 4);
  if (clean.length === 4) localStorage.setItem(KEY_ADMIN_PIN, clean);
}
export function clearAdminPin() {
  localStorage.removeItem(KEY_ADMIN_PIN);
  sessionStorage.removeItem(KEY_ADMIN_UNLOCK);
}
export function verifyAdminPin(pin) {
  return getAdminPin() === (pin || "").replace(/\D/g, "").slice(0, 4);
}
export function isAdminUnlocked() {
  if (!hasAdminPin()) return false;  // gate required only when PIN set
  const t = parseInt(sessionStorage.getItem(KEY_ADMIN_UNLOCK) || "0", 10);
  return t && (Date.now() - t) < UNLOCK_TTL_MS;
}
export function markAdminUnlocked() {
  sessionStorage.setItem(KEY_ADMIN_UNLOCK, String(Date.now()));
}
export function lockAdmin() {
  sessionStorage.removeItem(KEY_ADMIN_UNLOCK);
}

// Wake word (say "Sakhi Didi" to start the mic hands-free)
export function getWakeWord() { return localStorage.getItem(KEY_WAKE) === "1"; }
export function setWakeWord(on) {
  if (on) localStorage.setItem(KEY_WAKE, "1");
  else localStorage.removeItem(KEY_WAKE);
}

// Speech pace (Sarvam TTS pace param: 0.9x slower, 1.0x default, 1.1x faster)
export function getSpeechPace() {
  const v = parseFloat(localStorage.getItem(KEY_PACE) || String(DEFAULT_PACE));
  return isNaN(v) ? DEFAULT_PACE : v;
}
export function setSpeechPace(v) {
  const n = parseFloat(v);
  if (!isNaN(n)) localStorage.setItem(KEY_PACE, String(n));
}

// Custom wake phrase (defaults to "sakhi didi"). Case-insensitive substring match.
export function getWakePhrase() {
  return (localStorage.getItem(KEY_WAKE_PHRASE) || DEFAULT_WAKE_PHRASE).toLowerCase().trim();
}
export function setWakePhrase(p) {
  const clean = (p || "").trim();
  if (!clean) localStorage.removeItem(KEY_WAKE_PHRASE);
  else localStorage.setItem(KEY_WAKE_PHRASE, clean);
}
// Build the individual "trigger tokens" that any single one can wake the mic.
// - the full phrase
// - each word ≥ 3 chars
export function getWakeTokens() {
  const phrase = getWakePhrase();
  const tokens = new Set([phrase]);
  phrase.split(/\s+/).forEach((w) => { if (w.length >= 3) tokens.add(w); });
  return Array.from(tokens);
}

// Barge-in hint show-count (per kiosk). Increments when hint is displayed.
export function shouldShowBargeHint() {
  const n = parseInt(localStorage.getItem(KEY_BARGE_HINT_SHOWN) || "0", 10);
  return n < BARGE_HINT_MAX;
}
export function noteBargeHintShown() {
  const n = parseInt(localStorage.getItem(KEY_BARGE_HINT_SHOWN) || "0", 10);
  localStorage.setItem(KEY_BARGE_HINT_SHOWN, String(n + 1));
}
export function resetBargeHint() {
  localStorage.removeItem(KEY_BARGE_HINT_SHOWN);
}

// Resize an image file to a max dimension, return JPEG dataURL.
export function fileToResizedDataUrl(file, maxDim = 900, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
